<template>
    <v-card flat class="pa-2">

        <!--Outcome Banner-->
        <v-card flat :color="banner.color" dark class="pa-2">
            <v-layout row align-center>
                <v-flex shrink class="pr-2">
                    <v-icon dark>{{ banner.icon }}</v-icon>
                </v-flex>
                <v-flex>
                    <div class="subheading font-weight-light">{{ banner.text }}</div>
                    <div class="caption" v-if="run.error != null && run.error != ''">{{ run.error }}</div>
                </v-flex>
            </v-layout>
        </v-card>

        <!--Summary Counts-->
        <v-layout row wrap align-center class="pt-2" v-if="counts != null">
            <v-flex shrink class="pr-2 pb-1">
                <v-chip small color="green" text-color="white">
                    {{ counts.rowsImported }} rows imported
                </v-chip>
            </v-flex>
            <v-flex shrink class="pr-2 pb-1">
                <v-chip small color="blue-grey" text-color="white">
                    {{ counts.rowsSkipped }} skipped
                </v-chip>
            </v-flex>
            <v-flex shrink class="pr-2 pb-1">
                <v-chip small :color="counts.rowsError > 0 ? 'red' : 'grey'" text-color="white">
                    {{ counts.rowsError }} row errors
                </v-chip>
            </v-flex>
            <v-flex shrink class="pr-2 pb-1">
                <v-chip small color="primary" text-color="white">
                    Folders: {{ counts.foldersDone }} / {{ counts.foldersTotal }}
                </v-chip>
            </v-flex>
            <v-flex shrink class="pr-2 pb-1" v-if="counts.foldersError > 0">
                <v-chip small color="red" text-color="white">
                    {{ counts.foldersError }} folder errors
                </v-chip>
            </v-flex>
        </v-layout>

        <!--Download-->
        <div class="pt-2">
            <v-btn
                    color="secondary"
                    class="ma-0"
                    :loading="downloading"
                    :disabled="downloading"
                    @click="$emit('download')"
            >
                <v-icon left>file_download</v-icon>
                Download Result Workbook (.xlsx)
            </v-btn>
            <div class="pt-1">
                <span class="caption greyType--text">Three sheets: Imported (audit record), Errors (what to fix), and Not Imported (re-import ready)</span>
            </div>
        </div>

        <v-flex class="text-xs-center pa-2" v-if="loadingReport">
            <v-progress-circular
                    :size="40"
                    :width="5"
                    color="primary"
                    indeterminate
            ></v-progress-circular>
        </v-flex>

        <template v-else-if="report != null">
            <!--Warnings-->
            <template v-if="warnings.length > 0">
                <div class="pt-2 pb-1">
                    <span class="caption orange--text">Warnings ({{ warnings.length }})</span>
                </div>
                <v-data-table
                        :headers="warningHeaders"
                        :items="warnings"
                        class="elevation-1 compact-table"
                        :rows-per-page-items="[10, 25, {text: 'All', value: -1}]"
                >
                    <template slot="items" slot-scope="props">
                        <td class="text-xs-right" style="width: 100px">{{ props.item.row_index }}</td>
                        <td>{{ props.item.folder }}</td>
                        <td>{{ props.item.message }}</td>
                    </template>
                </v-data-table>
            </template>

            <!--Row Errors-->
            <template v-if="rowErrors.length > 0">
                <div class="pt-2 pb-1">
                    <span class="caption red--text">Row Errors ({{ rowErrors.length }})</span>
                </div>
                <v-data-table
                        :headers="rowErrorHeaders"
                        :items="rowErrors"
                        class="elevation-1 compact-table"
                        :rows-per-page-items="[10, 25, {text: 'All', value: -1}]"
                >
                    <template slot="items" slot-scope="props">
                        <td class="text-xs-right" style="width: 100px">{{ props.item.row_index }}</td>
                        <td>{{ props.item.error }}</td>
                    </template>
                </v-data-table>
            </template>
        </template>

        <!--Start Another-->
        <div class="pt-2">
            <v-btn small flat color="primary" class="ma-0" @click="$emit('restart')">
                <v-icon small left>replay</v-icon>
                Start Another Import
            </v-btn>
        </div>
    </v-card>
</template>

<script>

    import {codeBaseMixin} from "../../codeBaseMixin";

    export default {
        mixins: [codeBaseMixin],
        props: {
            run: Object,
            report: Object,
            counts: Object,
            loadingReport: {
                type: Boolean,
                default: false
            },
            downloading: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                warningHeaders: [
                    {text: 'Row', value: 'row_index', align: 'right'},
                    {text: 'Folder', value: 'folder'},
                    {text: 'Warning', value: 'message', sortable: false}
                ],
                rowErrorHeaders: [
                    {text: 'Row', value: 'row_index', align: 'right'},
                    {text: 'Error', value: 'error', sortable: false}
                ]
            }
        },
        computed: {
            banner() {
                let status = this.run != null ? this.run.status : null;
                switch (status) {
                    case 'completed':
                        return {color: 'green', icon: 'check_circle', text: 'Import completed successfully'};
                    case 'completed_with_errors':
                        return {color: 'orange', icon: 'warning', text: 'Import completed with errors'};
                    case 'failed':
                        return {color: 'red', icon: 'error', text: 'Import failed'};
                    case 'cancelled':
                        return {color: 'blue-grey', icon: 'cancel', text: 'Import was cancelled'};
                    default:
                        return {color: 'blue-grey', icon: 'info', text: 'Run status: ' + status};
                }
            },
            warnings() {
                return this.report != null && this.report.warnings != null ? this.report.warnings : [];
            },
            rowErrors() {
                return this.report != null && this.report.rowErrors != null ? this.report.rowErrors : [];
            }
        }
    }
</script>

<style scoped>
    .compact-table >>> td, .compact-table >>> th {
        padding: 0 8px !important;
        height: 32px !important;
    }
</style>
